/**
 * The app's top breadcrumb for `/approvals/<id>`: "Approvals / <the approval's
 * plain title>" instead of "Approvals / Approvals". The routing map can only
 * hold a static element, so the element is this small component: it reads the
 * title the detail page published for the id in the URL (the approval's own
 * summary line) and says "Approval" until it is known. Kept tiny and free of
 * Apollo: the routing map sits on the app-shell boot path.
 */
import { useEffect } from 'react'
import { useT } from '@transifex/react'
import { useLocation } from 'react-router-dom'
import { create } from 'zustand'

const useTitles = create<{ titles: Record<string, string> }>(() => ({
  titles: {},
}))

/** The first line of the approval's summary, as the crumb reads it. */
export const crumbTitle = (summary: string | null | undefined): string =>
  (summary ?? '').split('\n')[0].trim()

/** The detail page publishes the approval's title for its id. */
export const usePublishApprovalTitle = (
  approvalId: string,
  summary: string | null | undefined
) => {
  const title = crumbTitle(summary)
  useEffect(() => {
    if (!title) return
    useTitles.setState((s) =>
      s.titles[approvalId] === title
        ? s
        : { titles: { ...s.titles, [approvalId]: title } }
    )
  }, [approvalId, title])
}

/** What the breadcrumb's last crumb shows for the approval in the URL. */
export function ApprovalCrumbTitle() {
  const t = useT()
  const { pathname } = useLocation()
  const titles = useTitles((s) => s.titles)
  const raw = pathname.split('/').filter(Boolean).pop() ?? ''
  let id = raw
  try {
    id = decodeURIComponent(raw)
  } catch {
    // keep the raw segment
  }
  return <>{titles[id] ?? t('Approval')}</>
}
